import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "./ui/card";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { useAuth, API } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { TrendingUp, BarChart3, PieChart as PieIcon, Trophy } from "lucide-react";

const STATUS_COLORS = {
  new: "#94a3b8",
  in_review: "#f59e0b",
  selected: "#6366f1",
  interview: "#0ea5e9",
  not_selected: "#f43f5e",
  hired: "#10b981",
  not_fit: "#1e293b",
};

export default function EmployerReports() {
  const { t } = useI18n();
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/employer/reports`, { headers: authHeader }).then(r => setData(r.data));
    // eslint-disable-next-line
  }, []);

  if (!data) return <div className="p-8 text-center text-slate-500">Loading reports…</div>;

  const pieData = data.status_breakdown.map(s => ({ name: t(`status.${s.status}`), value: s.count, key: s.status }));

  return (
    <div className="space-y-6" data-testid="employer-reports">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-base font-semibold text-slate-900 inline-flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#1A8FE3]" />Applications (30 days)</h3>
            <span className="text-xs text-slate-500">{data.totals.applications} total</span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.daily_applications}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval={4} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Line type="monotone" dataKey="count" stroke="#1A8FE3" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-display text-base font-semibold text-slate-900 inline-flex items-center gap-2 mb-4"><PieIcon className="h-4 w-4 text-[#1A8FE3]" />Status breakdown</h3>
          {pieData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-slate-400 text-sm">No applications yet</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={40} label={{ fontSize: 11 }}>
                    {pieData.map((e, i) => <Cell key={i} fill={STATUS_COLORS[e.key] || "#94a3b8"} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="font-display text-base font-semibold text-slate-900 inline-flex items-center gap-2 mb-4"><Trophy className="h-4 w-4 text-[#1A8FE3]" />Top jobs by applications</h3>
          {data.top_jobs.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-slate-400 text-sm">No applications yet</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.top_jobs} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="job" tick={{ fontSize: 10 }} width={120} />
                  <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                  <Bar dataKey="count" fill="#1A8FE3" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="font-display text-base font-semibold text-slate-900 inline-flex items-center gap-2 mb-4"><BarChart3 className="h-4 w-4 text-[#1A8FE3]" />Job performance</h3>
          {data.job_performance.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-slate-400 text-sm">No jobs yet</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.job_performance}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="title" tick={{ fontSize: 9 }} interval={0} angle={-20} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="views" fill="#94a3b8" name="Views" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="clicks" fill="#1A8FE3" name="Apply clicks" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="applications" fill="#10b981" name="Applications" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
